import { Box, useMediaQuery, useTheme } from "@mui/material";
import { BackwardBack, Fullscreen, Image, Motorcycle, SparePartKits, SpareParts } from "@pmag/icons";
import { Modal, Typography } from "@pmag/ui-components";
import { WishlistButton } from "@src/components/wishlistButton/WishlistButton";
import { type GQLComponentGroupOverviewModelFragment } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useChangeModel } from "@src/main/useChangeModel";
import { useRestart } from "@src/main/useRestart";
import { ArticleHeaderModelDetails } from "@src/pages/componentGroupOverview/components/articleHeaderModelDetails/ArticleHeaderModelDetails";
import { ArticleSubheader } from "@src/pages/componentGroupOverview/components/articleSubheader/ArticleSubheader";
import { dispatchSPFOnPowerPartsButtonClickEvent, dispatchSPFOnSparePartKitsButtonClickEvent } from "@src/utils/events";
import { type FunctionComponent, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
    BoxButtons,
    BoxColumn,
    BoxContainer,
    BoxContent,
    BoxDetailContainer,
    BoxGrid,
    BoxImage,
    BoxModelDetails,
    BoxPerfectFit,
    BoxSeparator,
    BoxVerticalSeparator,
    InspectButton,
    ModalImageBox,
    ModelImage,
    NavigationButton,
    StyledButton,
    StyledIconButton,
} from "./ArticleHeader.styles";

export const dataTestId = {
    container: "sparePartsFinder.articleHeader.container",
    modelImage: "sparePartsFinder.articleHeader.image",
    modelBrandName: "sparePartsFinder.articleHeader.brandName",
    modelDescription: "sparePartsFinder.articleHeader.description",
    buttonChangeModel: "sparePartsFinder.articleHeader.button.changeModel",
    buttonRestart: "sparePartsFinder.articleHeader.button.restart",
    buttonPowerParts: "sparePartsFinder.articleHeader.button.powerParts",
    buttonSparePartKits: "sparePartsFinder.articleHeader.button.sparePartKits",
};

interface ArticleHeaderProps {
    model?: GQLComponentGroupOverviewModelFragment | null;
}

export const ArticleHeader: FunctionComponent<ArticleHeaderProps> = ({ model }) => {
    const theme = useTheme();
    const { restart, showRestartButton } = useRestart();
    const { changeModel, showChangeModelButton } = useChangeModel();
    const { contextArticleId } = useGlobalState();
    const attributeConfig = useAttributeConfig();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1000));
    const [isHovering, setIsHovering] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const onPowerPartsClick = () => {
        if (model?.articleNumber) dispatchSPFOnPowerPartsButtonClickEvent(model.articleNumber);
    };

    const onSparePartKitsClick = () => {
        if (model?.articleNumber) dispatchSPFOnSparePartKitsButtonClickEvent(model.articleNumber);
    };

    const buttons = useMemo(
        () => (
            <BoxButtons>
                <NavigationButton
                    variant="text"
                    startIcon={<BackwardBack size="small" />}
                    data-testid={dataTestId.buttonRestart}
                    onClick={restart}
                    hidden={!showRestartButton}
                >
                    <Typography variant="body-short-01">
                        <FormattedMessage id="sparePartsFinder.articleHeader.button.restart" defaultMessage="Restart" />
                    </Typography>
                </NavigationButton>

                <NavigationButton
                    variant="text"
                    startIcon={<Motorcycle size="small" />}
                    data-testid={dataTestId.buttonChangeModel}
                    onClick={changeModel}
                    hidden={!showChangeModelButton}
                >
                    <Typography variant="body-short-01">
                        <FormattedMessage id="sparePartsFinder.articleHeader.button.changeModel" defaultMessage="Change model" />
                    </Typography>
                </NavigationButton>

                <WishlistButton />
            </BoxButtons>
        ),
        [changeModel, restart, showChangeModelButton, showRestartButton],
    );

    return (
        <BoxContainer data-testid={dataTestId.container}>
            {isSmallScreen && (
                <>
                    {buttons}

                    <BoxSeparator mb={theme.spacing(6)} />
                </>
            )}

            <BoxGrid>
                <BoxContent>
                    <BoxModelDetails>
                        {model?.assetPath && (
                            <BoxImage
                                onMouseOver={() => !isSmallScreen && setIsHovering(true)}
                                onMouseOut={() => !isSmallScreen && setIsHovering(false)}
                            >
                                <ModelImage src={model?.assetPath} data-testid={dataTestId.modelImage} isHovering={isHovering} />

                                {isHovering && (
                                    <InspectButton onClick={() => setIsModalOpen(true)}>
                                        <Fullscreen size={16} color={theme.palette.core.white} />
                                    </InspectButton>
                                )}
                            </BoxImage>
                        )}

                        <BoxDetailContainer>
                            <Typography variant="body-short-01" color={theme.palette.grey["600"]} data-testid={dataTestId.modelBrandName}>
                                {model?.brand?.name}
                            </Typography>

                            <Typography
                                variant={isSmallScreen ? "heading-08" : "heading-06"}
                                data-testid={dataTestId.modelDescription}
                                color={theme.palette.grey["800"]}
                            >
                                {model?.description}
                            </Typography>

                            <ArticleHeaderModelDetails
                                articleNumber={model?.articleNumber}
                                year={model?.year}
                                specifications={model?.modelSpecifications.map((spec) => spec.name)}
                            />
                        </BoxDetailContainer>

                        <StyledIconButton variant="ghost" onClick={() => setIsModalOpen(true)}>
                            <Fullscreen size={16} color={theme.palette.core.grey._80} />
                        </StyledIconButton>
                    </BoxModelDetails>

                    {contextArticleId && <ArticleSubheader />}

                    {!contextArticleId && (attributeConfig.showSparePartKitsButton || attributeConfig.showPowerPartsButton) && (
                        <BoxPerfectFit>
                            {!isSmallScreen && <BoxVerticalSeparator />}

                            <BoxColumn>
                                <Typography variant="label-02">
                                    <FormattedMessage
                                        id="sparePartsFinder.articleHeader.perfectFit.header"
                                        defaultMessage="perfect fit for this model:"
                                    />
                                </Typography>

                                <Box>
                                    {attributeConfig.showPowerPartsButton && (
                                        <StyledButton
                                            variant="text"
                                            startIcon={<SpareParts size="small" />}
                                            data-testid={dataTestId.buttonPowerParts}
                                            onClick={onPowerPartsClick}
                                        >
                                            <Typography variant="body-short-01">
                                                <FormattedMessage id="sparePartsFinder.articleHeader.button.powerParts" defaultMessage="PowerParts" />
                                            </Typography>
                                        </StyledButton>
                                    )}

                                    {attributeConfig.showSparePartKitsButton && (
                                        <StyledButton
                                            variant="text"
                                            startIcon={<SparePartKits size="small" />}
                                            data-testid={dataTestId.buttonSparePartKits}
                                            onClick={onSparePartKitsClick}
                                        >
                                            <Typography variant="body-short-01">
                                                <FormattedMessage
                                                    id="sparePartsFinder.articleHeader.button.sparePartKits"
                                                    defaultMessage="Spare Part Kits"
                                                />
                                            </Typography>
                                        </StyledButton>
                                    )}
                                </Box>
                            </BoxColumn>
                        </BoxPerfectFit>
                    )}
                </BoxContent>

                {!isSmallScreen && buttons}
            </BoxGrid>

            <Modal
                severity="info"
                showSeverityBorder={false}
                severityText={model?.description}
                severityIcon={<Image color={theme.palette.core.white} />}
                variant="background"
                open={isModalOpen}
                data-testid="sparePartsFinder.articleHeader.modal"
                onClose={() => {
                    setIsModalOpen(false);
                }}
            >
                {model?.assetPath && (
                    <ModalImageBox>
                        <ModelImage src={model?.assetPath} data-testid={dataTestId.modelImage} isHovering={false} />
                    </ModalImageBox>
                )}
            </Modal>
        </BoxContainer>
    );
};
